import { useDialogs } from '../components/ui'
import { Fragment, useEffect, useState, useCallback } from 'react'
import { renderApiService } from '../services/renderApiService'
import { ApiError } from '../services/BaseApiService'
import type { RenderJob } from '../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import { RenderJobStatus } from '../constants'
import SendForSignatureModal from '../components/esign/SendForSignatureModal'
import { isPasswordProtectedJob, isSignableRenderJob, isViewablePdfJob } from '../components/esign/esignShared'
import PdfViewerModal from '../components/pdf/PdfViewerModal'
import DocumentViewsDrawer from '../components/analytics/DocumentViewsDrawer'

const STATUS_OPTIONS = [
  { value: undefined,                    label: 'All' },
  { value: RenderJobStatus.COMPLETE,     label: 'Complete' },
  { value: RenderJobStatus.FAILED,       label: 'Failed' },
  { value: RenderJobStatus.PROCESSING,   label: 'Processing' },
  { value: RenderJobStatus.PENDING,      label: 'Pending' },
]

function statusLabel(id: number): string {
  return {
    [RenderJobStatus.PENDING]:    'Pending',
    [RenderJobStatus.PROCESSING]: 'Processing',
    [RenderJobStatus.COMPLETE]:   'Complete',
    [RenderJobStatus.FAILED]:     'Failed',
  }[id] ?? String(id)
}

function statusPillStyle(id: number): React.CSSProperties {
  const variants: Record<number, React.CSSProperties> = {
    [RenderJobStatus.PENDING]:    { background: colors.background,   color: colors.textSecondary, border: `1px solid ${colors.border}` },
    [RenderJobStatus.PROCESSING]: { background: colors.warningLight, color: colors.warning,       border: `1px solid ${colors.warningBorder}` },
    [RenderJobStatus.COMPLETE]:   { background: colors.successLight, color: colors.success,       border: `1px solid ${colors.successBorder}` },
    [RenderJobStatus.FAILED]:     { background: colors.errorLight,   color: colors.error,         border: `1px solid ${colors.errorBorder}` },
  }
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: radius.full,
    fontSize: fontSize.xs,
    fontWeight: 500,
    ...(variants[id] ?? { background: colors.background, color: colors.textSecondary, border: `1px solid ${colors.border}` }),
  }
}

function fmtBytes(n: number | null): string {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1_048_576).toFixed(1)} MB`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function parseWarnings(warningJson: string | null): string[] {
  if (!warningJson) return []
  try { return JSON.parse(warningJson) } catch { return [warningJson] }
}

export default function RenderJobsPage() {
  const dialogs = useDialogs()
  const [jobs, setJobs] = useState<RenderJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState<string | null>(null)
  const [sharing, setSharing] = useState<string | null>(null)
  const [rerendering, setRerendering] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [sharePassword, setSharePassword] = useState('')
  const [shareJobId, setShareJobId] = useState<string | null>(null)
  const [esignJobId, setEsignJobId] = useState<string | null>(null)
  const [viewsJobId, setViewsJobId] = useState<string | null>(null)
  const [viewJobId, setViewJobId] = useState<string | null>(null)

  const PAGE_SIZE = 50

  const load = useCallback(async (p: number, status: number | undefined, from: string, to: string) => {
    setLoading(true)
    try {
      const data = await renderApiService.listJobs({
        page: p, pageSize: PAGE_SIZE, status,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to + 'T23:59:59Z').toISOString() : undefined,
      })
      setJobs(data)
      setError('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(page, statusFilter, fromDate, toDate) }, [page, statusFilter, fromDate, toDate, load])

  function handleStatusChange(val: number | undefined) {
    setStatusFilter(val)
    setPage(1)
  }

  async function handleDownload(job: RenderJob) {
    setDownloading(job.renderJobId)
    try {
      const blob = await renderApiService.downloadOutput(job.renderJobId)
      const ext = job.renderStatusLookupId === RenderJobStatus.COMPLETE ? 'pdf' : 'bin'
      downloadBlob(blob, `render-${job.renderJobId.slice(0, 8)}.${ext}`)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Download failed')
    } finally {
      setDownloading(null)
    }
  }

  async function handleShare(job: RenderJob) {
    setShareJobId(job.renderJobId)
  }

  async function confirmShare() {
    if (!shareJobId) return
    setSharing(shareJobId)
    try {
      const { shareUrl } = await renderApiService.createShare(shareJobId, { password: sharePassword || undefined })
      await navigator.clipboard.writeText(shareUrl)
      setCopiedToken(shareJobId)
      setTimeout(() => setCopiedToken(null), 3000)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to create share link')
    } finally {
      setSharing(null)
      setShareJobId(null)
      setSharePassword('')
    }
  }

  async function handleRerender(job: RenderJob) {
    setRerendering(job.renderJobId)
    try {
      const result = await renderApiService.rerender(job.renderJobId)
      await load(page, statusFilter, fromDate, toDate)
      void dialogs.alert({ title: 'Re-render complete', message: `New job: ${result.renderJobId.slice(0, 8)}… (${result.pageCount ?? '?'} pages)` })
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Re-render failed')
    } finally {
      setRerendering(null)
    }
  }

  const actionBtn: React.CSSProperties = {
    padding: '3px 8px',
    background: 'none',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '11px',
    cursor: 'pointer',
    color: colors.text,
    whiteSpace: 'nowrap' as const,
  }

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontSize: fontSize.sm,
    fontWeight: 600,
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
    whiteSpace: 'nowrap',
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.sm,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  }

  return (
    <>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0, flex: 1 }}>
          Render Jobs
        </h1>
        <a
          href={renderApiService.exportJobsUrl({ status: statusFilter, from: fromDate ? new Date(fromDate).toISOString() : undefined, to: toDate ? new Date(toDate + 'T23:59:59Z').toISOString() : undefined })}
          download
          style={{ padding: '5px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text, textDecoration: 'none', background: 'none' }}
        >
          Export CSV
        </a>
      </div>

      <div style={{
        display: 'flex', gap: spacing.sm, marginBottom: spacing.lg, flexWrap: 'wrap', alignItems: 'center',
        background: colors.surface, border: `1px solid ${colors.border}`,
        borderRadius: radius.md, padding: spacing.md, boxShadow: shadow.xs,
      }}>
        {/* Status filter */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={String(opt.value)}
              onClick={() => handleStatusChange(opt.value)}
              style={{
                padding: '5px 12px',
                border: `1px solid ${statusFilter === opt.value ? colors.primary : colors.border}`,
                borderRadius: radius.full,
                background: statusFilter === opt.value ? colors.primary : colors.surface,
                color: statusFilter === opt.value ? colors.onPrimary : colors.textSecondary,
                fontSize: fontSize.sm,
                fontWeight: statusFilter === opt.value ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="date"
          value={fromDate}
          onChange={e => { setFromDate(e.target.value); setPage(1) }}
          style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text }}
        />
        <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={e => { setToDate(e.target.value); setPage(1) }}
          style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text }}
        />
        {(fromDate || toDate) && (
          <button onClick={() => { setFromDate(''); setToDate(''); setPage(1) }} style={{ fontSize: fontSize.xs, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
            Clear dates
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : jobs.length === 0 ? (
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          padding: spacing.xxl,
          textAlign: 'center',
          color: colors.textSecondary,
          boxShadow: shadow.sm,
        }}>
          <div style={{ fontSize: fontSize.lg, marginBottom: spacing.sm }}>No render jobs</div>
          <div style={{ fontSize: fontSize.base }}>
            {statusFilter != null ? 'No jobs match this filter.' : 'Render a template to see jobs here.'}
          </div>
        </div>
      ) : (
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          overflowX: 'auto',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Job ID</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Pages</th>
                <th style={thStyle}>Duration</th>
                <th style={thStyle}>Output</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const warnings = parseWarnings(job.warningJson)
                const isExpanded = expandedId === job.renderJobId
                return (
                  <Fragment key={job.renderJobId}>
                    <tr
                      onClick={() => warnings.length > 0 && setExpandedId(isExpanded ? null : job.renderJobId)}
                      style={{ cursor: warnings.length > 0 ? 'pointer' : 'default' }}
                    >
                      <td style={{ ...tdStyle, fontFamily: fontFamily.mono, fontSize: fontSize.xs }}>
                        {job.renderJobId.slice(0, 8)}…
                        {warnings.length > 0 && (
                          <span style={{
                            marginLeft: 6,
                            fontSize: fontSize['2xs'],
                            color: colors.warning,
                            fontFamily: fontFamily.sans,
                          }}>
                            ⚠ {warnings.length}
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={statusPillStyle(job.renderStatusLookupId)}>
                          {statusLabel(job.renderStatusLookupId)}
                        </span>
                        {job.errorMessage && (
                          <div style={{ fontSize: fontSize.xs, color: colors.error, marginTop: 2, maxWidth: 200 }}>
                            {job.errorMessage}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textSecondary }}>
                        {job.pageCount ?? '—'}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textSecondary }}>
                        {job.durationMs != null ? `${job.durationMs}ms` : '—'}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textSecondary }}>
                        {fmtBytes(job.outputByteSize)}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                        {new Date(job.createdAt).toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {job.renderStatusLookupId === RenderJobStatus.COMPLETE && (
                            <>
                              <button
                                onClick={e => { e.stopPropagation(); handleDownload(job) }}
                                disabled={downloading === job.renderJobId}
                                style={actionBtn}
                              >
                                {downloading === job.renderJobId ? '…' : 'Download'}
                              </button>
                              {isViewablePdfJob(job) && (
                                <button
                                  onClick={e => { e.stopPropagation(); setViewJobId(job.renderJobId) }}
                                  style={actionBtn}
                                  title={isPasswordProtectedJob(job) ? 'View the PDF (asks for its password)' : 'View the PDF'}
                                >
                                  View
                                </button>
                              )}
                              <button
                                onClick={e => { e.stopPropagation(); handleShare(job) }}
                                disabled={sharing === job.renderJobId}
                                style={actionBtn}
                                title="Copy shareable link to clipboard"
                              >
                                {copiedToken === job.renderJobId ? 'Copied!' : sharing === job.renderJobId ? '…' : 'Share'}
                              </button>
                              <button
                                onClick={e => { e.stopPropagation(); setViewsJobId(job.renderJobId) }}
                                style={actionBtn}
                                title="Who opened this document, when and where"
                              >
                                Views
                              </button>
                              {isSignableRenderJob(job) && (
                                <button
                                  onClick={e => { e.stopPropagation(); setEsignJobId(job.renderJobId) }}
                                  style={actionBtn}
                                  title="Send for signature"
                                >
                                  Send for signature
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); handleRerender(job) }}
                            disabled={rerendering === job.renderJobId}
                            style={{ ...actionBtn, opacity: rerendering === job.renderJobId ? 0.5 : 1 }}
                            title="Re-render using the original payload"
                          >
                            {rerendering === job.renderJobId ? '…' : 'Re-run'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && warnings.length > 0 && (
                      <tr key={`${job.renderJobId}-warnings`}>
                        <td colSpan={7} style={{
                          padding: `${spacing.sm} ${spacing.md}`,
                          background: colors.warningLight,
                          borderBottom: `1px solid ${colors.border}`,
                        }}>
                          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.warning, marginBottom: '4px' }}>
                            Warnings
                          </div>
                          <ul style={{ margin: 0, paddingLeft: spacing.lg }}>
                            {warnings.map((w, i) => (
                              <li key={i} style={{ fontSize: fontSize.sm, color: colors.warning }}>{w}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Share password modal */}
      {shareJobId && (
        <div style={{
          position: 'fixed', inset: 0, background: colors.overlay,
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => { setShareJobId(null); setSharePassword('') }}>
          <div style={{
            background: colors.surface, borderRadius: radius.md, padding: spacing.xl,
            boxShadow: shadow.lg, minWidth: 320, maxWidth: 400,
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: fontSize.base, marginBottom: spacing.md, color: colors.text }}>
              Create share link
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Password (optional)
            </div>
            <input
              type="password"
              value={sharePassword}
              onChange={e => setSharePassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmShare()}
              placeholder="Leave blank for no password"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', border: `1px solid ${colors.border}`,
                borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.md,
              }}
            />
            <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShareJobId(null); setSharePassword('') }}
                style={{ padding: '7px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', fontSize: fontSize.sm, cursor: 'pointer', color: colors.text }}
              >
                Cancel
              </button>
              <button
                onClick={confirmShare}
                disabled={sharing === shareJobId}
                style={{ padding: '7px 16px', border: 'none', borderRadius: radius.sm, background: colors.primary, color: colors.onPrimary, fontSize: fontSize.sm, cursor: 'pointer', fontWeight: 600 }}
              >
                {sharing === shareJobId ? 'Creating…' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {esignJobId && (
        <SendForSignatureModal renderJobId={esignJobId} onClose={() => setEsignJobId(null)} />
      )}
      {viewsJobId && <DocumentViewsDrawer renderJobId={viewsJobId} onClose={() => setViewsJobId(null)} />}

      {/* Pagination */}
      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md, justifyContent: 'flex-end' }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          style={{
            padding: '6px 14px',
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: fontSize.sm,
            cursor: 'pointer',
            color: colors.text,
            opacity: page === 1 ? 0.4 : 1,
          }}
        >
          Previous
        </button>
        <span style={{ padding: '6px 10px', fontSize: fontSize.sm, color: colors.textSecondary }}>
          Page {page}
        </span>
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={jobs.length < PAGE_SIZE || loading}
          style={{
            padding: '6px 14px',
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: fontSize.sm,
            cursor: 'pointer',
            color: colors.text,
            opacity: jobs.length < PAGE_SIZE ? 0.4 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
      <PdfViewerModal renderJobId={viewJobId} onClose={() => setViewJobId(null)} />
      {dialogs.element}
    </>
  )
}
